import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Steps through a list of beat durations once, then rests on the last beat.
 *
 * `index` is null on the server, on the first client render, under
 * `prefers-reduced-motion: reduce`, and when the element is already on screen
 * at mount (arming would hide rows the reader is looking at), which the caller
 * renders as the plain static table. Otherwise, after mount it arms at 0 and advances only while the element
 * is in view, the tab is visible and `active` holds; leaving any of those
 * pauses it on the current beat. `replay` starts it again from 0.
 */
export const usePlayOnce = <T extends HTMLElement>(durations: number[], active: boolean) => {
  const ref = useRef<T>(null);
  const [index, setIndex] = useState<number | null>(null);
  const [inView, setInView] = useState(false);
  const [pageVisible, setPageVisible] = useState(true);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const element = ref.current;
    if (!element || typeof IntersectionObserver === 'undefined') return;
    const box = element.getBoundingClientRect();
    if (box.top < window.innerHeight && box.bottom > 0) return;
    setIndex(0);
    // The same margin useInView uses for the site's other in-view reveals.
    const observer = new IntersectionObserver(
      (entries) => setInView(entries.some((entry) => entry.isIntersecting)),
      { rootMargin: '-12% 0px' },
    );
    observer.observe(element);
    const onVisibility = () => setPageVisible(document.visibilityState === 'visible');
    onVisibility();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      observer.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  const last = durations.length - 1;
  useEffect(() => {
    if (index === null || index >= last || !inView || !pageVisible || !active) return;
    const timer = window.setTimeout(() => setIndex(index + 1), durations[index]);
    return () => window.clearTimeout(timer);
  }, [index, last, inView, pageVisible, active, durations]);

  const replay = useCallback(() => setIndex(0), []);
  return { ref, index, replay };
};
